import React from 'react';
import { Link } from 'react-router-dom';
import { X, ShoppingCart, Heart } from 'lucide-react';
import { useCart } from '../contexts/CartContext';
import { toast } from '../hooks/use-toast';

const Compare = () => {
  const { compareList, removeFromCompare, clearCompare, addToCart } = useCart();

  const handleAddToCart = (product) => {
    addToCart(product);
    toast({
      title: 'Added to cart',
      description: `${product.name} has been added to your cart.`
    });
  };

  if (compareList.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-gray-400 mb-4 text-6xl">⚖️</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">No products to compare</h2>
          <p className="text-gray-600 mb-6">Add products to compare their features side by side.</p>
          <Link
            to="/products"
            className="inline-block bg-red-600 text-white px-8 py-3 rounded hover:bg-red-700 transition"
          >
            Browse Products
          </Link>
        </div>
      </div>
    );
  }

  // Common specifications across all products
  const specLabels = Array.from(
    new Set(compareList.flatMap(p => p.specifications?.map(s => s.label) || []))
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Compare Products</h1>
          {compareList.length > 0 && (
            <button
              onClick={clearCompare}
              className="text-red-600 hover:text-red-700 font-semibold"
            >
              Clear All
            </button>
          )}
        </div>

        <div className="bg-white rounded-lg shadow overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900 sticky left-0 bg-gray-50">
                  Product
                </th>
                {compareList.map((product) => (
                  <th key={product.id} className="px-6 py-4 text-center min-w-[250px]">
                    <div className="relative">
                      <button
                        onClick={() => removeFromCompare(product.id)}
                        className="absolute top-0 right-0 text-red-600 hover:text-red-700"
                      >
                        <X size={20} />
                      </button>
                      <Link to={`/product/${product.slug}`}>
                        <img
                          src={product.image || '/placeholder.jpg'}
                          alt={product.name}
                          onError={(e) => {
                            e.target.src = '/placeholder.jpg';
                          }}
                          className="w-full h-40 object-cover rounded mb-3"
                        />
                      </Link>
                      <Link
                        to={`/product/${product.slug}`}
                        className="font-semibold text-gray-900 hover:text-red-600 transition block"
                      >
                        {product.name}
                      </Link>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {/* Price */}
              <tr className="border-b hover:bg-gray-50">
                <td className="px-6 py-4 font-semibold text-gray-900 sticky left-0 bg-white">
                  Price
                </td>
                {compareList.map((product) => (
                  <td key={product.id} className="px-6 py-4 text-center">
                    <div className="font-bold text-gray-900 text-lg">${product.price.toFixed(2)}</div>
                    {product.originalPrice && (
                      <div className="text-sm text-gray-400 line-through">
                        ${product.originalPrice.toFixed(2)}
                      </div>
                    )}
                  </td>
                ))}
              </tr>

              {/* Brand */}
              <tr className="border-b hover:bg-gray-50">
                <td className="px-6 py-4 font-semibold text-gray-900 sticky left-0 bg-white">
                  Brand
                </td>
                {compareList.map((product) => (
                  <td key={product.id} className="px-6 py-4 text-center text-gray-600">
                    {product.brand}
                  </td>
                ))}
              </tr>

              {/* SKU */}
              <tr className="border-b hover:bg-gray-50">
                <td className="px-6 py-4 font-semibold text-gray-900 sticky left-0 bg-white">
                  SKU
                </td>
                {compareList.map((product) => (
                  <td key={product.id} className="px-6 py-4 text-center text-gray-600">
                    {product.sku}
                  </td>
                ))}
              </tr>

              {/* Rating */}
              <tr className="border-b hover:bg-gray-50">
                <td className="px-6 py-4 font-semibold text-gray-900 sticky left-0 bg-white">
                  Rating
                </td>
                {compareList.map((product) => (
                  <td key={product.id} className="px-6 py-4 text-center">
                    <div className="flex items-center justify-center">
                      <span className="text-yellow-400 mr-1">★</span>
                      <span className="font-semibold">{product.rating.toFixed(1)}</span>
                      <span className="text-gray-400 text-sm ml-1">({product.reviews})</span>
                    </div>
                  </td>
                ))}
              </tr>

              {/* Stock */}
              <tr className="border-b hover:bg-gray-50">
                <td className="px-6 py-4 font-semibold text-gray-900 sticky left-0 bg-white">
                  Stock
                </td>
                {compareList.map((product) => (
                  <td key={product.id} className="px-6 py-4 text-center">
                    {product.stock > 0 ? (
                      <span className="text-green-600 font-medium">In Stock ({product.stock})</span>
                    ) : (
                      <span className="text-red-600 font-medium">Out of Stock</span>
                    )}
                  </td>
                ))}
              </tr>

              {/* Specifications */}
              {specLabels.map((label) => (
                <tr key={label} className="border-b hover:bg-gray-50">
                  <td className="px-6 py-4 font-semibold text-gray-900 sticky left-0 bg-white">
                    {label}
                  </td>
                  {compareList.map((product) => {
                    const spec = product.specifications?.find(s => s.label === label);
                    return (
                      <td key={product.id} className="px-6 py-4 text-center text-gray-600">
                        {spec ? spec.value : '-'}
                      </td>
                    );
                  })}
                </tr>
              ))}

              {/* Actions */}
              <tr className="bg-gray-50">
                <td className="px-6 py-4 font-semibold text-gray-900 sticky left-0 bg-gray-50">
                  Action
                </td>
                {compareList.map((product) => (
                  <td key={product.id} className="px-6 py-4 text-center">
                    <button
                      onClick={() => handleAddToCart(product)}
                      disabled={product.stock === 0}
                      className="w-full bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 transition disabled:bg-gray-300 disabled:cursor-not-allowed mb-2 flex items-center justify-center space-x-2"
                    >
                      <ShoppingCart size={16} />
                      <span>Add to Cart q</span>
                    </button>
                    <Link
                      to={`/product/${product.slug}`}
                      className="block w-full border border-gray-300 px-4 py-2 rounded hover:bg-gray-50 transition"
                    >
                      View Details
                    </Link>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>

        {/* Continue Shopping */}
        <div className="mt-6">
          <Link
            to="/products"
            className="inline-flex items-center text-red-600 hover:text-red-700 font-semibold"
          >
            ← Continue Shopping
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Compare;
